import { Node, mergeAttributes } from "@tiptap/core";
import { VueNodeViewRenderer } from "@tiptap/vue-3";
import TooltipComponent from "./TooltipComponent.vue";

export const TooltipBlock = Node.create({
  name: "tooltipBlock",

  group: "block",

  content: "tooltipTitle tooltipBody",

  addAttributes() {
    return {
      iconType: {
        default: "hammer",
      },
      isOpen: {
        default: true,
      },
      title: {
        default: "Default Title",
      },
      body: {
        default: "Default Body",
      },
      blockId: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-block-id"),
        renderHTML: (attributes) => {
          return { "data-block-id": attributes.blockId };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: "div[data-tooltip-block]",
        getAttrs: (dom) => {
          return {
            iconType: dom.getAttribute("data-icon-type") || "hammer",
            isOpen: dom.hasAttribute("data-open"),
            title: dom.getAttribute("data-title") || "Default Title",
            body: dom.getAttribute("data-body") || "Default Body",
            blockId: dom.getAttribute("data-block-id"),
          };
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes, node }) {
    return [
      "div",
      mergeAttributes(HTMLAttributes, {
        "data-tooltip-block": "",
        "data-icon-type": HTMLAttributes.iconType,
        "data-block-id": node.attrs.blockId,
        class: `tooltip-block tooltip-${HTMLAttributes.iconType}`,
      }),
      [
        "div",
        { class: "tooltip-title-line" },
        [
          "img",
          {
            src: this.options.getIconPath(HTMLAttributes.iconType),
            class: "tooltip-icon",
            alt: `${HTMLAttributes.iconType} icon`,
          },
        ],
        [
          "div",
          { class: "tooltip-title", contenteditable: "false" },
          HTMLAttributes.title || "Default Title",
        ],
        [
          "div",
          { class: "accordion-arrow" },
          [
            "svg",
            {
              xmlns: "http://www.w3.org/2000/svg",
              width: "16",
              height: "16",
              viewBox: "0 0 24 24",
              fill: "none",
              stroke: "currentColor",
              "stroke-width": "2",
              "stroke-linecap": "round",
              "stroke-linejoin": "round",
            },
            ["polyline", { points: "6 9 12 15 18 9" }],
          ],
        ],
      ],
      [
        "div",
        { class: "tooltip-body-wrapper" },
        [
          "div",
          { class: "tooltip-body" },
          HTMLAttributes.body || "Default Body",
        ],
      ],
    ];
  },

  addOptions() {
    return {
      getIconPath: (iconType) => {
        if (iconType === "hammer") return "/images/icon/hammer.svg";
        if (iconType === "circle") return "/images/icon/circle.svg";
        if (iconType === "light") return "/images/icon/lightbulb.svg";
        return "";
      },
    };
  },

  addCommands() {
    return {
      setTooltipBlock:
        (attributes) =>
        ({ commands }) => {
          const { iconType, isOpen, title, body } = attributes;

          if (!iconType) {
            console.warn(
              "setTooltipBlock command requires iconType attribute."
            );
            return false;
          }

          // Insert the node with default title and body content
          return commands.insertContent({
            type: this.name,
            attrs: {
              iconType,
              isOpen: isOpen !== undefined ? isOpen : false,
              title: title || "Default Title",
              body: body || "Default Body",
            },
            content: [
              {
                type: "tooltipTitle",
                content: [
                  {
                    type: "text",
                    text: title, // Default title
                  },
                ],
              },
              {
                type: "tooltipBody",
                content: [
                  {
                    type: "paragraph",
                    content: [
                      {
                        type: "text",
                        text: body, // Default body
                      },
                    ],
                  },
                ],
              },
            ],
          });
        },
    };
  },

  addNodeView() {
    return VueNodeViewRenderer(TooltipComponent);
  },
});

// Define tooltipTitle node
export const TooltipTitle = Node.create({
  name: "tooltipTitle",
  group: "block",
  content: "inline*",
  defining: true,

  parseHTML() {
    return [
      {
        tag: "div.tooltip-title",
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "div",
      mergeAttributes(HTMLAttributes, { class: "tooltip-title" }),
      0,
    ];
  },
});

// Define tooltipBody node
export const TooltipBody = Node.create({
  name: "tooltipBody",
  group: "block",
  content: "block*",
  defining: true,

  parseHTML() {
    return [
      {
        tag: "div.tooltip-body",
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "div",
      mergeAttributes(HTMLAttributes, { class: "tooltip-body" }),
      0,
    ];
  },
});

export default TooltipBlock;
